import type { AreaDto, SignalDto } from '@nova/shared';
import { formatRelative } from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { useCreateFromSignal, useSignals, type SignalSort } from './useSignals.js';

/**
 * The scout's findings, ready to act on. Each carries a real source, and a
 * «Lag forslag» button that turns it into a proposal card the human then reworks.
 * A product-area filter keeps the list to the signals that matter to you.
 */
export function SignalsPanel({ take = 12 }: { take?: number }) {
  const [areaId, setAreaId] = useState('');
  const [sort, setSort] = useState<SignalSort>('newest');
  const { data: signals = [], isLoading } = useSignals({ take, areaId, sort });
  const { data: areas = [] } = useQuery({
    queryKey: ['areas'],
    queryFn: () => api.get<AreaDto[]>('/areas'),
    staleTime: 5 * 60_000,
  });

  const selectClass =
    'border-hairline bg-base/50 text-ink-soft h-8 rounded-lg border px-2 text-xs';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap justify-end gap-2">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SignalSort)}
          aria-label="Sorter signaler"
          className={selectClass}
        >
          <option value="newest">Nyeste først</option>
          <option value="relevance">Mest relevant</option>
        </select>
        <select
          value={areaId}
          onChange={(e) => setAreaId(e.target.value)}
          aria-label="Filtrer signaler på produktområde"
          className={selectClass}
        >
          <option value="">Alle produktområder</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <SkeletonPanel lines={4} />
      ) : signals.length === 0 ? (
        <EmptyState
          title={
            areaId
              ? 'Ingen nye signaler for dette området.'
              : 'Ingen nye signaler ennå. Omverdensovervåkeren kjører hverdager 06:00.'
          }
        />
      ) : (
        signals.map((signal) => <SignalCard key={signal.id} signal={signal} />)
      )}
    </div>
  );
}

function SignalCard({ signal }: { signal: SignalDto }) {
  const navigate = useNavigate();
  const create = useCreateFromSignal();

  return (
    <section
      className="border-hairline bg-base/30 rounded-lg border p-3"
      style={signal.area ? { borderLeftColor: signal.area.color, borderLeftWidth: 2 } : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink min-w-0 text-sm">{signal.title}</p>
        <span className="text-ink-soft shrink-0 text-[11px]">{formatRelative(signal.createdAt)}</span>
      </div>

      {signal.suggestedAngle && (
        <p className="text-ink-soft mt-1.5 text-[13px] leading-relaxed">{signal.suggestedAngle}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <a
          href={signal.sourceUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent inline-flex items-center gap-1 text-[11px]"
        >
          {signal.sourceName ?? 'Kilde'} <ExternalLink size={11} aria-hidden />
        </a>
        {signal.area && <span className="text-ink-soft text-[11px]">{signal.area.name}</span>}
        <span className="text-ink-soft text-[11px]">Relevans {signal.relevance}/5</span>

        <Button
          size="sm"
          variant="primary"
          className="ml-auto"
          icon={<Sparkles size={13} aria-hidden />}
          disabled={!signal.area}
          title={signal.area ? undefined : 'Signalet mangler område — lag kortet manuelt.'}
          loading={create.isPending}
          onClick={() =>
            create.mutate(signal.id, { onSuccess: (res) => navigate(`/plan/${res.id}`) })
          }
        >
          Lag forslag
        </Button>
      </div>

      {create.isError && (
        <p className="text-danger mt-1.5 text-xs">{(create.error as Error).message}</p>
      )}
    </section>
  );
}
